import { useEffect } from "react";
import { Outlet, useParams } from "react-router-dom";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { Alert } from "@mui/material";
import Snackbar from "@mui/material/Snackbar";
import { auth } from "@/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { closeSnackbar, setActiveOrganization } from "@/store/uiSlice";
import { getProfile, isLoggedInSelector, setUser } from "@/features/auth/slice";
import { useFetchOrganizationsByAliasQuery } from "@/features/organizations/api/organizationsApi";

type Params = {
  organizationId?: string;
  organizationAlias?: string;
};

const GlobalRoute = () => {
  const dispatch = useAppDispatch();
  const isLoggedIn = useAppSelector(isLoggedInSelector);
  const snackbar = useAppSelector((state) => state.ui.snackbar);
  const { organizationId, organizationAlias } = useParams<Params>();

  const aliasQuery = useFetchOrganizationsByAliasQuery(organizationAlias ?? skipToken, {
    skip: !organizationAlias,
  });

  useEffect(() => {
    if (organizationAlias && aliasQuery?.data) {
      dispatch(setActiveOrganization(aliasQuery.data.organizationId));
    } else if (organizationId) {
      dispatch(setActiveOrganization(organizationId));
    }
  }, [aliasQuery.data, dispatch, organizationAlias, organizationId]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user: any) => {
      if (user) {
        dispatch(
          setUser({
            uid: user.uid,
            email: user.email,
            emailVerified: user.emailVerified,
          })
        );
        dispatch(getProfile(user.uid));
      }
    });

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  return (
    <>
      <Outlet />
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        open={snackbar.isOpen}
        onClose={() => dispatch(closeSnackbar())}
        autoHideDuration={4000}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default GlobalRoute;
